import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { createApiResponse, createErrorApiResponse, withErrorHandling } from "@entrilia/shared/api/axiosHelper.ts";
import type { ApiResponse } from "@entrilia/shared/api/types.ts";
import useFetch from "@entrilia/shared/hooks/useFetch.ts";
import { logError } from "@entrilia/shared/logging.ts";
import type {
  FailedMeasure,
  FinancialStatementResponse,
  ReportField,
  RunFinancialStatementReportRequest,
} from "../../../../../../../api/types/svTypes.ts";
import { useEnhancedBiApiClientProvider } from "../../../contexts/ApiClientProviderContext.ts";
import { useBiCommonDataContext } from "../../../contexts/BiCommonDataContext.ts";
import { selectSessionId } from "../../../store/selectors/currentReportSelectors.ts";
import { selectLogQueries } from "../../../store/selectors/devToolsSelectors.ts";
import { isFinancialStatementConfigurationValid } from "../../../utilities/isConfigurationValid.ts";
import ChangePreviewModeButton from "../../builder/common/ChangePreviewModeButton.tsx";
import useCommonReportHandlers from "../../builder/common/hooks/useCommonReportHandlers.ts";
import BiErrorCodeComponent from "../common/bi-error/BiErrorCodeComponent.tsx";
import PreviewActions from "../common/PreviewActions.tsx";
import PreviewComponentAlert from "../common/PreviewComponentAlert.tsx";
import ValidationState from "../common/ValidationState.tsx";
import ViewContainer from "../common/ViewContainer.tsx";
import ViewerParameters from "../common/ViewerParameters.tsx";
import type { FinancialStatementRunState } from "../storage/runState.ts";
import { getStatementLineFieldCaption } from "../storage/runStateUtils.ts";
import FinancialStatementGrid from "./FinancialStatementGrid.tsx";
import { createFieldCaptionLookup } from "./statementGridModel.ts";

interface Props {
  runState: FinancialStatementRunState;
}

const noReportFields: ReportField[] = [];
const noFailedMeasures: FailedMeasure[] = [];

const FinancialStatementViewerContainer = ({ runState }: Props) => {
  const { runFinancialStatementReport } = useEnhancedBiApiClientProvider();
  const { conditions, reportId, dataSetId, dimensions } = useBiCommonDataContext();
  const sessionId = useSelector(selectSessionId);
  const isQueryLoggingActive = useSelector(selectLogQueries);
  const { setEtag, updateDevTools } = useCommonReportHandlers(noReportFields);

  const configurationValidity = useMemo(
    () => isFinancialStatementConfigurationValid(conditions, runState, dimensions),
    [conditions, dimensions, runState]
  );

  const request = useMemo(
    (): RunFinancialStatementReportRequest => ({
      conditions: [
        ...conditions.map((condition) => ({ ...condition.config, invalid: condition.validation?.invalid })),
        ...runState.lineFields.flatMap((field) =>
          field.config.useCustomConditions ? (field.config.customConditions ?? []) : []
        ),
      ].filter((condition) => condition.parameter === true),
      reportId: reportId ?? "",
      sessionId,
      withDrillDown: false,
      useQueryLogging: isQueryLoggingActive === true,
    }),
    [conditions, isQueryLoggingActive, reportId, runState.lineFields, sessionId]
  );

  const getFieldCaption = useMemo(
    () => createFieldCaptionLookup([...runState.lineFields.map((field) => field.meta), ...dimensions]),
    [dimensions, runState.lineFields]
  );

  const getParameterCaption = useCallback(
    (field: ReportField) => getStatementLineFieldCaption(field, runState.lines, getFieldCaption),
    [getFieldCaption, runState.lines]
  );

  const validationFields = useMemo(
    () => runState.lineFields.map((field) => ({ config: field.config, meta: { caption: getParameterCaption(field) } })),
    [getParameterCaption, runState.lineFields]
  );

  const runStatement = useCallback(
    async (signal?: AbortSignal): Promise<ApiResponse<FinancialStatementResponse>> => {
      if (!runFinancialStatementReport) {
        throw new Error("runFinancialStatementReport is not supported");
      }
      setEtag(undefined, undefined);
      const [response, apiError] = await withErrorHandling(runFinancialStatementReport)(dataSetId, request, signal);
      if (apiError) {
        if (!apiError.isCanceledRequest) {
          logError(apiError, "[FinancialStatementViewerContainer] runFinancialStatementReport");
        }
        return createErrorApiResponse<FinancialStatementResponse>(apiError);
      }
      return createApiResponse(response);
    },
    [dataSetId, request, runFinancialStatementReport, setEtag]
  );

  const handleStatementLoaded = useCallback(
    (response: FinancialStatementResponse) => {
      if (response.errorCode) {
        return;
      }
      updateDevTools(response.loggingItems);
      setEtag(response.etag, undefined);
    },
    [setEtag, updateDevTools]
  );

  const [statement, fetchError, { isFetching, fetch: refetchStatement }] = useFetch(runStatement, {
    disabled: !configurationValidity.valid,
    onSetData: handleStatementLoaded,
  });

  const handleRefresh = useCallback(() => {
    void refetchStatement();
  }, [refetchStatement]);

  const conditionConfigurations = useMemo(() => conditions.map((condition) => condition.config), [conditions]);
  const errorMessage = fetchError?.message ?? statement?.errorCode;

  return (
    <ViewContainer
      Parameters={
        runState.showParameters ? (
          <ViewerParameters
            fieldsWithConditions={runState.fieldsWithCustomConditionParameters}
            initialDetailedView={runState.settings.detailedView}
            getFieldCaption={getParameterCaption}
          />
        ) : (
          <></>
        )
      }
      Preview={
        <BiErrorCodeComponent errorMessage={errorMessage}>
          <PreviewComponentAlert error={errorMessage} failedMeasures={statement?.failedMeasures ?? noFailedMeasures} />
          <ValidationState state={configurationValidity} fields={validationFields}>
            <FinancialStatementGrid
              response={configurationValidity.valid && !errorMessage ? statement : undefined}
              lines={runState.lines}
              dataColumns={runState.columns.dataColumns}
              conditions={conditionConfigurations}
              settings={runState.settings}
              getFieldCaption={getFieldCaption}
              loading={isFetching}
            />
          </ValidationState>
        </BiErrorCodeComponent>
      }
      ActionPanel={<PreviewActions onRefresh={handleRefresh} ChangePreviewModeButton={<ChangePreviewModeButton />} />}
    />
  );
};

export default FinancialStatementViewerContainer;
